
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Club {
  id: string;
  name: string;
  sport?: string;
}

interface Organization {
  id: string;
  name: string;
  description?: string;
  logo_url?: string;
  website?: string;
  created_at: string;
  updated_at: string;
  clubs?: Club[];
}

export const useOrganizationManagement = () => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [hoveredOrgId, setHoveredOrgId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    logo_url: '',
    website: '',
  });

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: organizations, isLoading } = useQuery({
    queryKey: ['organizations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select(`
          *,
          clubs (
            id,
            name,
            sport
          )
        `)
        .order('name');
      
      if (error) {
        console.error('Error fetching organizations:', error);
        throw error;
      }
      return data as Organization[];
    },
  });

  const createOrgMutation = useMutation({
    mutationFn: async (orgData: typeof formData) => {
      const { error } = await supabase
        .from('organizations')
        .insert([orgData]);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      toast.success('Organization created successfully');
      setIsCreateDialogOpen(false);
      resetForm();
    },
    onError: (error) => {
      toast.error('Failed to create organization');
      console.error('Error:', error);
    },
  });

  const updateOrgMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof formData }) => {
      const { error } = await supabase
        .from('organizations')
        .update(data)
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      toast.success('Organization updated successfully');
      setEditingOrg(null);
      resetForm();
    },
    onError: (error) => {
      toast.error('Failed to update organization');
      console.error('Error:', error);
    },
  });

  const deleteOrgMutation = useMutation({
    mutationFn: async (id: string) => {
      
      try {
        // First, let's verify the organization exists and get its current state
        const { data: orgCheck, error: orgCheckError } = await supabase
          .from('organizations')
          .select('id, name')
          .eq('id', id);
        
        if (orgCheckError) {
          console.error('Error checking organization:', orgCheckError);
          throw new Error(`Error checking organization: ${orgCheckError.message}`);
        }
        
        if (!orgCheck || orgCheck.length === 0) {
          throw new Error('Organization not found');
        }
        
        // Check for clubs - be more thorough
        const { data: clubs, error: clubsError } = await supabase
          .from('clubs')
          .select('id, name')
          .eq('organization_id', id);
        
        if (clubsError) {
          console.error('Error checking clubs:', clubsError);
          throw new Error(`Database error while checking clubs: ${clubsError.message}`);
        }
        
        if (clubs && clubs.length > 0) {
          const clubNames = clubs.map(c => c.name).join(', ');
          throw new Error(`Cannot delete organization with existing clubs. Please delete the following clubs first: ${clubNames}`);
        }
        
        // Check for organization memberships
        const { data: memberships, error: membershipsError } = await supabase
          .from('organization_memberships')
          .select('id, user_id')
          .eq('organization_id', id);
        
        if (membershipsError) {
          console.error('Error checking memberships:', membershipsError);
          throw new Error(`Database error while checking memberships: ${membershipsError.message}`);
        }
        
        // Delete memberships first if they exist
        if (memberships && memberships.length > 0) {
          const { error: deleteMembershipsError } = await supabase
            .from('organization_memberships')
            .delete()
            .eq('organization_id', id);
          
          if (deleteMembershipsError) {
            console.error('Error deleting memberships:', deleteMembershipsError);
            throw new Error(`Failed to delete organization memberships: ${deleteMembershipsError.message}`);
          }
        }
        
        // Now attempt to delete the organization - with detailed error handling
        const { data: deleteResult, error: deleteError, count } = await supabase
          .from('organizations')
          .delete()
          .eq('id', id)
          .select();
        
        
        if (deleteError) {
          // Check if it's a foreign key constraint error
          if (deleteError.code === '23503') {
            throw new Error(`Cannot delete organization due to foreign key constraint. There may be related records that need to be deleted first. Error: ${deleteError.message}`);
          }
          throw new Error(`Failed to delete organization: ${deleteError.message} (Code: ${deleteError.code})`);
        }
        
        if (!deleteResult || deleteResult.length === 0) {
          // Double-check if the organization still exists
          const { data: stillExists } = await supabase
            .from('organizations')
            .select('id')
            .eq('id', id);
          
          if (stillExists && stillExists.length > 0) {
            throw new Error('Organization was not deleted - check database permissions and constraints');
          }
        }
        
        return { success: true, deletedId: id, deletedData: deleteResult };
        
      } catch (error) {
        console.error('Organization deletion failed:', error);
        throw error;
      }
    },
    onSuccess: (data) => {
      // Immediately update the cache by removing the deleted organization
      queryClient.setQueryData(['organizations'], (oldData: Organization[] | undefined) => {
        if (!oldData) return oldData;
        const filtered = oldData.filter(org => org.id !== data.deletedId);
        return filtered;
      });
      
      // Also invalidate and refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      queryClient.refetchQueries({ queryKey: ['organizations'] });
      
      toast.success('Organization deleted successfully');
    },
    onError: (error: Error) => {
      console.error('❌ Delete organization mutation error:', error);
      if (error.message.includes('Cannot delete organization with existing clubs')) {
        toast.error(error.message);
      } else if (error.message.includes('foreign key constraint')) {
        toast.error('Cannot delete organization due to existing relationships. Please check for related records.');
      } else {
        toast.error(`Failed to delete organization: ${error.message}`);
      }
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      logo_url: '',
      website: '',
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingOrg) {
      updateOrgMutation.mutate({ id: editingOrg.id, data: formData });
    } else {
      createOrgMutation.mutate(formData);
    }
  };

  const handleEdit = (org: Organization) => {
    setEditingOrg(org);
    setFormData({
      name: org.name,
      description: org.description || '',
      logo_url: org.logo_url || '',
      website: org.website || '',
    });
  };

  const handleDelete = (id: string) => {
    // Find the organization name for better confirmation dialog
    const org = organizations?.find(o => o.id === id);
    const orgName = org ? org.name : 'this organization';
    
    if (window.confirm(`Are you sure you want to delete "${orgName}"? This action cannot be undone.`)) {
      deleteOrgMutation.mutate(id);
    }
  };

  const handleManageMembers = (orgId: string) => {
    navigate(`/superadmin/org/${orgId}`);
  };

  return {
    organizations,
    isLoading,
    isCreateDialogOpen,
    setIsCreateDialogOpen,
    editingOrg,
    setEditingOrg,
    hoveredOrgId,
    setHoveredOrgId,
    formData,
    setFormData,
    resetForm,
    handleSubmit,
    handleEdit,
    handleDelete,
    handleManageMembers,
    isCreating: createOrgMutation.isPending,
    isUpdating: updateOrgMutation.isPending,
    isDeleting: deleteOrgMutation.isPending,
  };
};
